import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { pendingSubmitReset } from '../store/slices/pending-slice';
import { submitPendingThunk } from '../store/thunks/pending-thunks';
import {
  ENVELOPE_FIELDS_BY_TYPE,
  PLATFORM_IDS,
  serializeEnvelope,
  type ContentEnvelope,
  type ContentType,
  type PurchaseLink,
} from '../lib/content/envelope-builder';

/**
 * Manual pending-content submission form.
 *
 * The form is type-aware: after picking `fact` / `news` / `release`
 * the shown fields adjust to what the corresponding consumer-surface
 * card renders. The values feed into a JSON envelope that the
 * backend adapter decodes on the read path so approved content
 * projects into a proper `<FactSection>` / `<NewsItem>` /
 * `<ReleaseCard>` instead of a bare body string.
 *
 * The top-level pending `source` field is provenance (who or what
 * submitted the item) and is separate from the envelope's `source`
 * (citation displayed on the rendered card).
 */

interface FormState {
  readonly type: ContentType;
  readonly provenance: string;
  readonly title: string;
  readonly body: string;
  readonly image: string;
  readonly platforms: ReadonlyArray<string>;
  readonly galleryText: string;
  readonly videosText: string;
  readonly purchaseLinks: ReadonlyArray<PurchaseLink>;
  readonly gameId: string;
  readonly source: string;
  readonly sourceUrl: string;
}

const INITIAL_FORM: FormState = {
  type: 'news',
  provenance: 'manual-admin',
  title: '',
  body: '',
  image: '',
  platforms: [],
  galleryText: '',
  videosText: '',
  purchaseLinks: [],
  gameId: '',
  source: '',
  sourceUrl: '',
};

/** Split a newline / comma-separated list into a trimmed string array. */
function splitList(raw: string): ReadonlyArray<string> {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function buildEnvelope(form: FormState): ContentEnvelope {
  return {
    title: form.title,
    body: form.body,
    image: form.image,
    gallery: splitList(form.galleryText),
    videos: splitList(form.videosText),
    platforms: form.platforms,
    purchaseLinks: form.purchaseLinks,
    gameId: form.gameId,
    source: form.source,
    sourceUrl: form.sourceUrl,
  };
}

export default function AdminSubmitPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const submitStatus = useAppSelector((state) => state.pending.submitStatus);
  const submitError = useAppSelector((state) => state.pending.submitError);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);

  useEffect(() => {
    dispatch(pendingSubmitReset());
  }, [dispatch]);

  useEffect(() => {
    if (submitStatus === 'succeeded') navigate('/admin/pending');
  }, [submitStatus, navigate]);

  const patch = (next: Partial<FormState>): void => setForm({ ...form, ...next });

  const relevantFields = useMemo(
    () => new Set(ENVELOPE_FIELDS_BY_TYPE[form.type]),
    [form.type],
  );

  const envelopeJson = useMemo(() => serializeEnvelope(buildEnvelope(form)), [form]);
  const canSubmit =
    form.body.trim().length > 0 &&
    form.provenance.trim().length > 0 &&
    submitStatus !== 'loading';

  const togglePlatform = (v: string, checked: boolean): void => {
    if (checked) {
      patch({ platforms: [...form.platforms, v] });
    } else {
      patch({ platforms: form.platforms.filter((p) => p !== v) });
    }
  };

  const addPurchaseLink = (): void => {
    patch({ purchaseLinks: [...form.purchaseLinks, { vendor: '', url: '' }] });
  };
  const updatePurchaseLink = (i: number, next: Partial<PurchaseLink>): void => {
    patch({
      purchaseLinks: form.purchaseLinks.map((p, idx) => (idx === i ? { ...p, ...next } : p)),
    });
  };
  const removePurchaseLink = (i: number): void => {
    patch({ purchaseLinks: form.purchaseLinks.filter((_, idx) => idx !== i) });
  };

  const handleSubmit = useCallback(
    (evt: FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      void dispatch(
        submitPendingThunk({
          type: form.type,
          source: form.provenance.trim(),
          body: envelopeJson,
        }),
      );
    },
    [dispatch, envelopeJson, form.provenance, form.type],
  );

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <CardContent>
        <SeoHead
          route="/admin/submit"
          pageTitle="Submit content"
          branding={brandingFixture.share}
          jsonLdType="WebSite"
        />
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem', mb: 2 }}>
          Submit pending content
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fields adjust based on the selected content type. Items auto-approve 12 hours
          after submission unless approved or rejected first. The body is stored as a
          JSON envelope so the consumer-facing cards render with images, links, and
          citations after approval.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="type-label">Type</InputLabel>
                <Select
                  labelId="type-label"
                  label="Type"
                  value={form.type}
                  onChange={(e) => patch({ type: e.target.value as ContentType })}
                >
                  <MenuItem value="fact">Fact of the day</MenuItem>
                  <MenuItem value="news">News</MenuItem>
                  <MenuItem value="release">Release</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Provenance"
                required
                fullWidth
                value={form.provenance}
                onChange={(e) => patch({ provenance: e.target.value })}
                helperText="Who or what submitted this item (e.g. manual-admin, ingestion-pipeline)."
              />
            </Stack>

            {relevantFields.has('title') && (
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => patch({ title: e.target.value })}
                helperText={form.type === 'release' ? 'Release name' : 'Headline'}
              />
            )}

            <TextField
              label="Body"
              required
              multiline
              minRows={5}
              value={form.body}
              onChange={(e) => patch({ body: e.target.value })}
              helperText={
                form.type === 'news'
                  ? 'Summary ≤300 chars, ideally including an "According to <source>..." citation.'
                  : 'Main content text.'
              }
            />

            {relevantFields.has('image') && (
              <TextField
                label="Image URL"
                value={form.image}
                onChange={(e) => patch({ image: e.target.value })}
                helperText="Hero image (JPG/PNG/WebP). 16:9 preferred for releases."
              />
            )}

            {relevantFields.has('platforms') && (
              <FormControl component="fieldset">
                <FormLabel component="legend">Platforms</FormLabel>
                <FormGroup row>
                  {PLATFORM_IDS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={form.platforms.includes(option.value)}
                          onChange={(e) => togglePlatform(option.value, e.target.checked)}
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            )}

            {relevantFields.has('gallery') && (
              <TextField
                label="Gallery URLs"
                multiline
                minRows={2}
                value={form.galleryText}
                onChange={(e) => patch({ galleryText: e.target.value })}
                helperText="One URL per line (or comma-separated). Rendered on the game-detail page."
              />
            )}

            {relevantFields.has('videos') && (
              <TextField
                label="Video URLs"
                multiline
                minRows={2}
                value={form.videosText}
                onChange={(e) => patch({ videosText: e.target.value })}
                helperText="YouTube or direct mp4 URLs, one per line."
              />
            )}

            {relevantFields.has('purchaseLinks') && (
              <PurchaseLinksSection
                purchaseLinks={form.purchaseLinks}
                onAdd={addPurchaseLink}
                onUpdate={updatePurchaseLink}
                onRemove={removePurchaseLink}
              />
            )}

            {relevantFields.has('gameId') && (
              <TextField
                label="Game id"
                value={form.gameId}
                onChange={(e) => patch({ gameId: e.target.value })}
                helperText={
                  form.type === 'release'
                    ? 'Stable slug that ties this release to its game-detail page (e.g. subnautica-2).'
                    : 'Optional. Links this article to a specific game page.'
                }
              />
            )}

            {relevantFields.has('source') && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Source (citation)"
                  fullWidth
                  value={form.source}
                  onChange={(e) => patch({ source: e.target.value })}
                  helperText='Displayed as "According to <source>..." on the rendered card.'
                />
                <TextField
                  label="Source URL"
                  fullWidth
                  value={form.sourceUrl}
                  onChange={(e) => patch({ sourceUrl: e.target.value })}
                  helperText="Link to the primary source article."
                />
              </Stack>
            )}

            <Divider />
            <EnvelopePreview envelopeJson={envelopeJson} />

            {submitStatus === 'failed' && (
              <Alert severity="error">{submitError ?? 'Submission failed.'}</Alert>
            )}
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              {submitStatus === 'loading' ? 'Submitting…' : 'Submit'}
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

interface PurchaseLinksSectionProps {
  readonly purchaseLinks: ReadonlyArray<PurchaseLink>;
  readonly onAdd: () => void;
  readonly onUpdate: (i: number, next: Partial<PurchaseLink>) => void;
  readonly onRemove: (i: number) => void;
}

function PurchaseLinksSection(props: PurchaseLinksSectionProps): JSX.Element {
  return (
    <Box>
      <FormLabel component="legend" sx={{ mb: 1, display: 'block' }}>
        Purchase links
      </FormLabel>
      <Stack spacing={1.5}>
        {props.purchaseLinks.map((entry, i) => (
          <Stack key={`purchase-${i}`} direction="row" spacing={1}>
            <TextField
              label="Vendor"
              size="small"
              value={entry.vendor}
              onChange={(e) => props.onUpdate(i, { vendor: e.target.value })}
              sx={{ width: 180 }}
            />
            <TextField
              label="URL"
              size="small"
              value={entry.url}
              onChange={(e) => props.onUpdate(i, { url: e.target.value })}
              sx={{ flexGrow: 1 }}
            />
            <IconButton onClick={() => props.onRemove(i)} aria-label={`remove purchase link ${i + 1}`}>
              <CloseIcon />
            </IconButton>
          </Stack>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={props.onAdd}
          size="small"
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
        >
          Add purchase link
        </Button>
      </Stack>
    </Box>
  );
}

function EnvelopePreview({ envelopeJson }: { readonly envelopeJson: string }): JSX.Element {
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="overline" color="text.secondary">
          Envelope preview
        </Typography>
        <Chip label={`${envelopeJson.length} chars`} size="small" variant="outlined" />
      </Stack>
      <Box
        component="pre"
        sx={{
          bgcolor: 'action.hover',
          p: 1.5,
          borderRadius: 1,
          overflow: 'auto',
          fontSize: '0.75rem',
          maxHeight: 200,
          m: 0,
        }}
      >
        {envelopeJson}
      </Box>
    </Box>
  );
}
